import type { Action } from '@reduxjs/toolkit';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
} from '@iconik-io/core/src/date/date_formats';
import {
  httpDeleteSystemDomainsBySystemDomainIdLogo,
  httpPostSystemDomainsBySystemDomainIdLogo,
} from '@iconik-io/iconik-api/src/services/auth';
import {
  httpPatchGroupByGroupId,
  httpPatchTeamByTeamId,
  httpPatchUserByUserId,
  type SettingsMergedSettingsSchemaNullable,
  type SettingsSystemSettingPublicSchemaPartial,
  type SettingsUserSettingSchemaPartial,
} from '@iconik-io/iconik-api/src/services/settings';
import { httpDeleteUsersInvitePurge } from '@iconik-io/iconik-api/src/services/users';
import {
  DEFAULT_DISPLAY_FIELDS,
  DEFAULT_FACET_FIELDS,
} from '@iconik-io/shared-lib/src/constants/search';
import {
  updateGroupSettings,
  updateMergedSettings,
  updateSystemSettings,
  updateUserSettings,
} from '@iconik-io/shared-lib/src/redux/settings/slice';
import {
  getGroupSettings as getSharedGroupSettings,
  getMergedSettings as getSharedMergedSettings,
  getSystemSettings as getSharedSystemSettings,
  getTeamSettings as getSharedTeamSettings,
  getUserSettings as getSharedUserSettings,
  modifySystemSettings as modifySharedSystemSettings,
} from '@iconik-io/shared-lib/src/redux/settings/thunks';

import {
  LS_ASSETS_DEFAULT_PANELS,
  LS_SEARCH_DEFAULT_PANELS,
} from '../../constants/data';
import type { AppDispatch, GetState } from '../../store/types';
import urlAppendTimestamp from '../../utils/url_append_timestamp';
import { segmentTrackPortalsFeatureChanged } from '../analytics_tracking/thunks';
import {
  setAssetDefaultPanels,
  setDateFormat,
  setDateTimeFormat,
  setLogo,
  setSearchDefaultPanels,
} from '../app/slice';
import { isAssetSection, isSearchSection } from '../app/types';

export {
  getGroupsBySearchViewSetting,
  modifyGroupSettingsRequest,
  modifyTeamSettingsRequest,
  updateSearchViewSettingForGroups,
} from '@iconik-io/shared-lib/src/redux/settings/thunks';

export function setAssetPanels(panelsArray: string[] | null | undefined) {
  return (dispatch: AppDispatch, getState: GetState): Action | boolean => {
    const { breakpoint } = getState().displayReducer;
    const isMobile = breakpoint < 3;
    if (isMobile) {
      return false;
    }
    const panelsToSet = (panelsArray ?? []).filter(isAssetSection);
    localStorage.setItem(LS_ASSETS_DEFAULT_PANELS, JSON.stringify(panelsToSet));
    return dispatch(setAssetDefaultPanels(panelsToSet));
  };
}

export function setSearchPanels(panelsArray: string[] | null | undefined) {
  return (dispatch: AppDispatch): Action => {
    const panelsToSet = (panelsArray ?? []).filter(isSearchSection);
    localStorage.setItem(LS_SEARCH_DEFAULT_PANELS, JSON.stringify(panelsToSet));
    return dispatch(setSearchDefaultPanels(panelsToSet));
  };
}

function parseArrayFromLocalStorage(key: string): string[] | undefined {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return undefined;
    }
    const result = JSON.parse(item);
    if (Array.isArray(result)) {
      return result;
    }
  } catch {}

  return undefined;
}

export function getMergedSettings(
  callback?: (setting: SettingsMergedSettingsSchemaNullable) => void,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    return dispatch(getSharedMergedSettings())
      .then((mergedSettings) => {
        const settings = getState().settings.merged;
        const state = getState();

        // Always update date formats, even when null/undefined (to handle user clearing custom formats)
        dispatch(setDateFormat(settings.date_format || DEFAULT_DATE_FORMAT));
        dispatch(
          setDateTimeFormat(
            settings.datetime_format || DEFAULT_DATE_TIME_FORMAT,
          ),
        );

        const assetsPanels = parseArrayFromLocalStorage(
          LS_ASSETS_DEFAULT_PANELS,
        );
        if (assetsPanels) {
          dispatch(setAssetPanels(assetsPanels));
        } else {
          dispatch(setAssetPanels(settings.asset_default_sections));
        }

        const searchPanels = parseArrayFromLocalStorage(
          LS_SEARCH_DEFAULT_PANELS,
        );
        if (searchPanels) {
          dispatch(setSearchPanels(searchPanels));
        } else {
          dispatch(setSearchPanels(settings.search_default_sections));
        }

        if (settings.logo_url) {
          dispatch(setLogo(settings.logo_url));
        } else {
          dispatch(setLogo(state.appReducer.iconikLogo));
        }

        // Create new object with defaults to avoid mutation
        const settingsWithDefaults: SettingsMergedSettingsSchemaNullable = {
          ...settings,
          facet_fields:
            !settings.facet_fields || !settings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : settings.facet_fields,
          search_display_fields:
            !settings.search_display_fields ||
            !settings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : settings.search_display_fields,
        };

        dispatch(updateMergedSettings({ settings: settingsWithDefaults }));

        if (callback && typeof callback === 'function') {
          callback(settingsWithDefaults);
        }

        return mergedSettings;
      })
      .catch((error) => {
        dispatch(setDateFormat(DEFAULT_DATE_FORMAT));
        dispatch(setDateTimeFormat(DEFAULT_DATE_TIME_FORMAT));
        dispatch(setLogo(getState().appReducer.iconikLogo));
        throw error; // Propagate error to caller
      });
  };
}

export const updateSystemLogo = (data: { logo: Blob }) => {
  const formData = new window.FormData();
  formData.append('logo', data.logo, 'logo.png');
  return (dispatch: AppDispatch, getState: GetState) => {
    const systemDomainId = getState().authReducer.userData.system_domain_id;
    if (!systemDomainId) {
      return;
    }
    dispatch(
      httpPostSystemDomainsBySystemDomainIdLogo(systemDomainId, formData),
    ).then((response) => {
      if (!response.body.logo) {
        return;
      }
      const logoUrl = urlAppendTimestamp(response.body.logo);
      dispatch(setLogo(logoUrl));
      dispatch(updateSystemSettings({ settings: { logo_url: logoUrl } }));
    });
  };
};

export function clearSystemLogo() {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    dispatch(updateSystemSettings({ settings: { logo_url: '' } }));
    const systemDomainId = getState().authReducer.userData.system_domain_id;
    if (!systemDomainId) {
      return Promise.resolve();
    }
    return dispatch(
      httpDeleteSystemDomainsBySystemDomainIdLogo(systemDomainId),
    ).then(() => dispatch(getMergedSettings()));
  };
}

export function getSystemSettings() {
  return (dispatch: AppDispatch, getState: GetState) => {
    return dispatch(getSharedSystemSettings()).then(() => {
      const systemSettings = getState().settings.system;
      if (systemSettings) {
        const settingsWithDefaults = {
          ...systemSettings,
          facet_fields:
            !systemSettings.facet_fields || !systemSettings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : systemSettings.facet_fields,
          search_display_fields:
            !systemSettings.search_display_fields ||
            !systemSettings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : systemSettings.search_display_fields,
        };
        dispatch(updateSystemSettings({ settings: settingsWithDefaults }));
      }
    });
  };
}

export function getUserSettings(userId: string | undefined) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const id = userId || getState().authReducer.userData.id;
    return dispatch(getSharedUserSettings(id)).then(() => {
      const userSettings = getState().settings.user[id];
      if (userSettings) {
        const settingsWithDefaults = {
          ...userSettings,
          facet_fields:
            !userSettings.facet_fields || !userSettings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : userSettings.facet_fields,
          search_display_fields:
            !userSettings.search_display_fields ||
            !userSettings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : userSettings.search_display_fields,
        };
        dispatch(
          updateUserSettings({ userId: id, settings: settingsWithDefaults }),
        );
      }
    });
  };
}

export function getGroupSettings(groupId: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    return dispatch(getSharedGroupSettings(groupId)).then(() => {
      const groupSettings = getState().settings.group[groupId];
      if (groupSettings) {
        const settingsWithDefaults = {
          ...groupSettings,
          facet_fields:
            !groupSettings.facet_fields || !groupSettings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : groupSettings.facet_fields,
          search_display_fields:
            !groupSettings.search_display_fields ||
            !groupSettings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : groupSettings.search_display_fields,
        };
        dispatch(
          updateGroupSettings({ groupId, settings: settingsWithDefaults }),
        );
      }
    });
  };
}

export function getTeamSettings(teamId: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    return dispatch(getSharedTeamSettings(teamId)).then(() => {
      const teamSettings = getState().settings.group[teamId];
      if (teamSettings) {
        const settingsWithDefaults = {
          ...teamSettings,
          facet_fields:
            !teamSettings.facet_fields || !teamSettings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : teamSettings.facet_fields,
          search_display_fields:
            !teamSettings.search_display_fields ||
            !teamSettings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : teamSettings.search_display_fields,
        };
        dispatch(
          updateGroupSettings({
            groupId: teamId,
            settings: settingsWithDefaults,
          }),
        );
      }
    });
  };
}

export const modifyUserSettings =
  (id: string | undefined, data: SettingsUserSettingSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const userId = id || getState().authReducer.userData.id;
    const systemSettings = getState().settings.system;

    // Replace empty arrays with system defaults (user should inherit from system)
    const dataToSave: any = { ...data };
    if (Array.isArray(data.facet_fields) && data.facet_fields.length === 0) {
      dataToSave.facet_fields =
        systemSettings.facet_fields || DEFAULT_FACET_FIELDS;
    }
    if (
      Array.isArray(data.search_display_fields) &&
      data.search_display_fields.length === 0
    ) {
      dataToSave.search_display_fields =
        systemSettings.search_display_fields || DEFAULT_DISPLAY_FIELDS;
    }

    return dispatch(httpPatchUserByUserId(userId, dataToSave)).then(
      (response) => {
        const settings = response.body;
        const settingsWithDefaults = {
          ...settings,
          facet_fields:
            settings.facet_fields === null ||
            !settings.facet_fields ||
            !settings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : settings.facet_fields,
          search_display_fields:
            settings.search_display_fields === null ||
            !settings.search_display_fields ||
            !settings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : settings.search_display_fields,
        };
        dispatch(
          updateUserSettings({ userId, settings: settingsWithDefaults }),
        );
        return dispatch(getMergedSettings());
      },
    );
  };

export const modifyGroupSettings =
  (groupId: string, data: SettingsUserSettingSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const systemSettings = getState().settings.system;

    // Replace empty arrays with system defaults (group should inherit from system)
    const dataToSave: any = { ...data };
    if (Array.isArray(data.facet_fields) && data.facet_fields.length === 0) {
      dataToSave.facet_fields =
        systemSettings.facet_fields || DEFAULT_FACET_FIELDS;
    }
    if (
      Array.isArray(data.search_display_fields) &&
      data.search_display_fields.length === 0
    ) {
      dataToSave.search_display_fields =
        systemSettings.search_display_fields || DEFAULT_DISPLAY_FIELDS;
    }
    if (dataToSave.default_upload_storage_id === '') {
      dataToSave.default_upload_storage_id = null;
    }

    return dispatch(httpPatchGroupByGroupId(groupId, dataToSave)).then(
      (response) => {
        const settings = response.body;
        const settingsWithDefaults = {
          ...settings,
          facet_fields:
            settings.facet_fields === null ||
            !settings.facet_fields ||
            !settings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : settings.facet_fields,
          search_display_fields:
            settings.search_display_fields === null ||
            !settings.search_display_fields ||
            !settings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : settings.search_display_fields,
        };
        dispatch(
          updateGroupSettings({ groupId, settings: settingsWithDefaults }),
        );
        return dispatch(getMergedSettings());
      },
    );
  };

export const modifyTeamSettings =
  (teamId: string, data: SettingsUserSettingSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const systemSettings = getState().settings.system;

    // Replace empty arrays with system defaults (team should inherit from system)
    const dataToSave: any = { ...data };
    if (Array.isArray(data.facet_fields) && data.facet_fields.length === 0) {
      dataToSave.facet_fields =
        systemSettings.facet_fields || DEFAULT_FACET_FIELDS;
    }
    if (
      Array.isArray(data.search_display_fields) &&
      data.search_display_fields.length === 0
    ) {
      dataToSave.search_display_fields =
        systemSettings.search_display_fields || DEFAULT_DISPLAY_FIELDS;
    }
    if (dataToSave.default_upload_storage_id === '') {
      dataToSave.default_upload_storage_id = null;
    }

    return dispatch(httpPatchTeamByTeamId(teamId, dataToSave)).then(
      (response) => {
        const settings = response.body;
        const settingsWithDefaults = {
          ...settings,
          facet_fields:
            settings.facet_fields === null ||
            !settings.facet_fields ||
            !settings.facet_fields.length
              ? DEFAULT_FACET_FIELDS
              : settings.facet_fields,
          search_display_fields:
            settings.search_display_fields === null ||
            !settings.search_display_fields ||
            !settings.search_display_fields.length
              ? DEFAULT_DISPLAY_FIELDS
              : settings.search_display_fields,
        };
        dispatch(
          updateGroupSettings({
            groupId: teamId,
            settings: settingsWithDefaults,
          }),
        );
        return dispatch(getMergedSettings());
      },
    );
  };

export const modifySystemSettings =
  (data: SettingsSystemSettingPublicSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const storedSettings = getState().settings.system;
    const hasSubmittedPortals = 'enable_portals' in data;
    // `settings.system` is merged into and starts empty, so an absent key means
    // the settings never loaded rather than that the feature is off.
    const hasStoredPortalsValue = 'enable_portals' in storedSettings;
    const wasPortalsEnabled = Boolean(storedSettings.enable_portals);
    const isPortalsEnabled = Boolean(data.enable_portals);

    return dispatch(modifySharedSystemSettings(data)).then(() => {
      if (
        hasSubmittedPortals &&
        hasStoredPortalsValue &&
        isPortalsEnabled !== wasPortalsEnabled
      ) {
        dispatch(segmentTrackPortalsFeatureChanged(isPortalsEnabled));
      }
      return dispatch(getMergedSettings());
    });
  };

// Helper function for modifying current user settings
export const modifyCurrentUserSettings =
  (data: SettingsUserSettingSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const userId = getState().authReducer.userData.id;
    return dispatch(modifyUserSettings(userId, data));
  };

export function purgeInviteLinks() {
  return (dispatch: AppDispatch) => {
    return dispatch(httpDeleteUsersInvitePurge());
  };
}
